import React from "react";
import { observer } from "mobx-react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Chip,
  CircularProgress,
} from "@mui/material";
import { SignatureStatusRow } from "./EditionSignatureStatusCard";

/**
 * Confirmación final antes de Enviar a Firmar: es el último paso manual del
 * usuario — al confirmar da fe de que la documentación está correcta y el
 * resto del circuito (firmas → DJCT → congelación → ejemplar → DNDA → SADAIC)
 * sigue automáticamente. Muestra la vista final de QUIÉN firma QUÉ (incluida
 * la DJCT futura, que nace al cierre de contratos), sin detalles internos de
 * cada contrato.
 *
 * Los datos salen de `signatureStatuses` (query editionSignatureStatus, que
 * pre-envío llena los firmantes desde el signersSnapshot de cada documento y
 * agrega la fila virtual de la DJCT) — la misma fuente del Estado de Firmas.
 */

const isDjctRow = (row: SignatureStatusRow) =>
  row.documentType === "djct" || row.contractName.includes("DJCT");

const SignerLine: React.FC<{ name: string; email: string }> = ({ name, email }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, pl: 2 }}>
    <Typography variant="body2">{name}</Typography>
    <Typography variant="caption" color="text.secondary">
      {email}
    </Typography>
  </Box>
);

const DocumentSigners: React.FC<{ row: SignatureStatusRow }> = ({ row }) => {
  const authors = row.recipients.filter((r) => (r.order ?? 1) <= 1);
  const editors = row.recipients.filter((r) => (r.order ?? 1) >= 2);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {row.contractName}
      </Typography>
      {row.recipients.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
          Sin firmantes definidos
        </Typography>
      ) : (
        <>
          {authors.map((r, i) => (
            <SignerLine key={`a${i}`} name={r.name} email={r.email} />
          ))}
          {editors.map((r, i) => (
            <SignerLine key={`e${i}`} name={r.name} email={r.email} />
          ))}
        </>
      )}
    </Box>
  );
};

export const EditionSendForSignatureDialog: React.FC<{
  open: boolean;
  signatureStatuses: SignatureStatusRow[];
  processing?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = observer(({ open, signatureStatuses, processing = false, onCancel, onConfirm }) => {
  const contracts = signatureStatuses.filter((r) => !isDjctRow(r));
  const djct = signatureStatuses.find(isDjctRow);

  return (
    <Dialog open={open} onClose={processing ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Confirmar envío a firma</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Se envían a firmar ahora {contracts.length === 1 ? "1 contrato" : `${contracts.length} contratos`}:
        </Typography>
        {contracts.map((row) => (
          <DocumentSigners key={row.editionDocumentationId} row={row} />
        ))}

        {djct && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {djct.contractName}
              </Typography>
              <Chip
                size="small"
                label="Se enviará una vez firmado el último contrato"
                sx={{ backgroundColor: "#e3f2fd", color: "#1565c0" }}
              />
            </Box>
            {djct.recipients.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                Sin firmantes definidos: falta un representante firmante en la editorial propia
              </Typography>
            ) : (
              djct.recipients.map((r, i) => (
                <SignerLine key={i} name={r.name} email={r.email} />
              ))
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={processing}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={processing || contracts.length === 0}
          startIcon={processing ? <CircularProgress size={16} /> : undefined}
        >
          Enviar a Firmar
        </Button>
      </DialogActions>
    </Dialog>
  );
});
